/// <amd-module name="VSS/Platform/Contribution" />
import { ICompletedContribution, IVssContentService } from "./Content";
import { IVssObservableService, IVssPageContext, IVssPageContextInternal, Services, VssObservableService, IVssRestClientOptions } from "./Context";
import { ClientDataProviderQuery, DataProviderExceptionDetails, DataProviderResult } from "./Contracts/Contributions";
import { IWebContributionsRestClient } from "./RestClient/WebContributions";
import { deepMergeWithOptions } from "./Util/Object";
import { readJsonScript } from "./Util/Serialization";

/**
 * An IContributedService is a contribution that refers to an IVssService. This
 * MAY be an ms.vss-web.service or it MAY be some other type that acts as a service
 * but tracks other properties.
 *
 * A contributed service can define a set of dependencies which are contributionIds
 * or a uri for non-trusted contributions that implements the service in an frame.
 */
export interface IContributedService {
    /**
     * The name of the service that implements the command. The service MUST be
     * registered with this name in the global Services registery.
     */
    serviceName: string;

    /**
     * The service can define a set of dependencies that need to be resolved before
     * the service is created. This allows the service to get other contributions
     * executed, like content, or data providers before starting.
     */
    dependencies?: string[];

    /**
     * URI to the HTML content which registers the service(s).
     *
     * This is required for services that are run in an IFrame instead of inside
     * the initial page frame.
     */
    uri?: string;

    /**
     * Optional configuration which can be consumed in the extension's initial
     * configuration object.
     */
    configuration?: any;
}

/**
 * Services that implement contributed actions will expose an execute method which
 * will implement functionality for the action.
 */
export interface IContributedActionService {
    /**
     * execute is called to invoke the action
     *
     * @param context - Context information to pass to the aciton
     */
    execute: (context: {} | undefined) => void;
}

/**
 * An IContributedCommand represents a named action that consumers can call.
 * Commands allow the producer and consumer to work with each other to execute
 * code via a named service and method.
 *
 * This abstraction allows the two parties to not know how to interact directly
 * with each other or take dependencies directly on each other. This facilities
 * delay loading of command implementations until the command is required.
 */
export interface IContributedCommand extends IContributedService {
    /**
     * The name the method that implements the command.
     */
    methodName: string;

    /**
     * Optional arguments to pass to the method
     */
    methodArguments?: any[];
}

/**
 * Services that implement command execution functions MAY implent a checkStatus call
 * for commands that have a variable status based on the pages current state.
 *
 * Before commands are shown or executed the status of the command is checked.
 */
export interface ICommandStatusService {
    /**
     * checkStatus is called with the name of a command. The service MAY choose to
     * return an ICommandStatus for the command which allows the service to control
     * aspects of how the command is treated.
     *
     * @param commandId - Name of the command the status is being requested for.
     *
     * @returns The ICommandStatus for the requested commandId if the command is
     *  registered. If the command isn't registered null is returned.
     */
    checkStatus: (commandId: string) => ICommandStatus | null;
}

/**
 * This is the result of a checkStatus call and defines status of a given command
 * for the current state of the page.
 */
export interface ICommandStatus {
    /**
     * disbaled should be returned true if the command is not available at this time.
     */
    disabled?: boolean;

    /**
     * hidden defines whether the command should be shown to the user if the user
     * requests a set of available commands.
     */
    hidden?: boolean;
}

/**
 * Service for resolving IVssService's remotely.
 */
export interface IRemoteServiceProvider {
    /**
     * Get a contributed service by its contribution id and contributed service definition.
     *
     * @param contributionId - Id of the contribution that defines this service
     * @param contributedService - definition of the service to retrieve.
     */
    getService: <T>(contributionId: string, contributedService: IContributedService) => Promise<T | undefined>;
}

/**
 * An IServerException is the marshalled form of an exception that occurred on
 * the server. Depending on the server configuration and other details the set
 * of information may vary.
 */
export interface IServerException {
    /**
     * The fully qualified type name of the exception that was thrown.
     */
    exceptionType: string;

    /**
     * The human readable message that describes the reason for the exception.
     */
    message: string;

    /**
     * Details about where the exception occurred in the code.
     */
    stackTrace?: string;
}

/**
 * Event fired when an asynchronous contribution query is about to be issued to the server.
 * Allows event handlers to add properties to send to the server.
 */
export interface IDataProviderQueryStartingEvent {
    /**
     * Id of the data provider being fetched
     */
    contributionIds: string[];

    /**
     * Properties being sent to the data provider
     */
    properties: { [key: string]: any };
}

/**
 * When a contribution query is executed the resulting data provider updated internally
 * and once this is complete, observers will be notified through an event with the
 * resulting data.
 */
export interface IDataProviderQueryCompletedEvent {
    /**
     * Data that was resolved for dataProviders that were executed during the query.
     */
    dataProviders?: { [contributionId: string]: any };

    /**
     * Shared data that was resolved during the execution of the query.
     */
    dataProviderSharedData?: { [key: string]: any };

    /**
     * Exceptions that occurred running data providers.
     */
    dataProviderExceptions?: { [key: string]: DataProviderExceptionDetails };
}

/**
 * Details for a contribution provider (e.g. an extension)
 */
export interface IContributionProviderDetails {
    /**
     * Id of the contribution provider (e.g. the extension id)
     */
    id: string;

    /**
     * Display name for the contribution provider
     */
    displayName: string;

    /**
     * Version of the provider
     */
    version: string;

    /**
     * Base URI for the provider content
     */
    baseUri: string;

    /**
     * Optional fallback URI for the provider content (i.e. if BaseUri points to a CDN)
     */
    fallbackBaseUri?: string;
}

/**
 * Events fired by the contribution service.
 */
export const enum ContributionServiceEvent {
    /**
     * Starting is fired before the query is executed. This allows subscribes to augment
     * the request with additional details.
     */
    DataProviderQueryStarting = "dpqStarting",

    /**
     * Completed is fired once a successful request is completed with the details about
     * the data that was returned.
     */
    DataProviderQueryCompleted = "dpqCompleted"
}

/**
 * The IContributionService is used to interact with the contribution system.
 *
 * - This allows clients to get data returned from data providers.
 * - Run commands defined within services.
 * - Determine the set of components that have been registered
 * - Interact with all other contributions related to the current pageContext.
 *
 * Events:
 *  dpqStarting - IDataProviderQueryStartingEvent
 *  dpqCompleted - IDataProviderQueryCompletedEvent
 */
export interface IVssContributionService
    extends IVssObservableService<
    IDataProviderQueryStartingEvent | IDataProviderQueryCompletedEvent,
    ContributionServiceEvent.DataProviderQueryStarting | ContributionServiceEvent.DataProviderQueryCompleted
    > {
    /**
     * A consumer may execute a command by supplying the commandId for the command and
     * the set of arguments required to perform the operation.
     *
     * @param commandId - Name of the command that should be executed.
     *
     * @param args - Variable set of arguments the caller is supplying to the
     *  command for execution. The caller MUST know what the command requires
     *  given the service doesn't have the ability to enforce this.
     *
     * @returns - A Promise<T> where T is the return type expected from the execution
     *  of the command. null is returned if the requested command isn't registered.
     */
    executeCommand: <T>(commandId: string, ...args: any[]) => Promise<T | null>;

    /**
     * A consumer may execute a command by supplying the contributed command and
     * the set of arguments required to perform the operation.
     *
     * @param contributionId - Id of the contribution that defines this command
     *
     * @param command - A contributed command that should be executed.
     *
     * @param args - Variable set of arguments the caller is supplying to the
     *  command for execution. The caller MUST know what the command requires
     *  given the service doesn't have the ability to enforce this.
     *
     * @returns - A Promise<T> where T is the return type expected from the execution
     *  of the command. null is returned if the requested command isn't registered.
     */
    executeCommandEx: <T>(contributionId: string, command: IContributedCommand, ...args: any[]) => Promise<T | null>;

    /**
     * getContribution is used to retrieve an inlined contribution that was sent to the
     * client.
     *
     * @param contributionId The unique identifier for the contribution.
     *
     * @returns The contribution properties if the contribution is available otherwise undefined.
     */
    getContribution: <T>(contributionId: string) => T | undefined;

    /**
     * getContributionAsync will retrieve the contribution from the local values if it has been
     * retrieved, but if not it will make a call to the server to get the contribution and any
     * related contributions.
     *
     * @param contributionId The unique identifier for the contribution.
     *
     * @param requestParameters Parameters to use when fetching data.
     *
     * @returns A promise to the contribution. If the promise cant be retrieved the promise will fail.
     */
    getContributionAsync: <T>(contributionId: string, requestParameters?: { [key: string]: any }, ignoreCache?: boolean) => Promise<T | undefined>;

    /**
     * getContributionsAsync will retrieve contributions from the local values if they have been
     * retrieved, but if not it will make a call to the server to get the missing contributions and any
     * related contributions.
     *
     * @param contributionIds The unique identifiers for the contributions.
     *
     * @param requestParameters Parameters to use when fetching data.
     *
     * @returns A promise to the contributions. If the promise cant be retrieved the promise will fail.
     */
    getContributionsAsync: <T>(
        contributionIds: string[],
        requestParameters?: { [key: string]: any },
        ignoreCache?: boolean
    ) => Promise<Array<T | undefined>>;

    /**
     * getContributionIdsByType is used to get a set of contribution Ids based on the type of
     * the contribution. This will return all contribution Ids that are currently available
     * on the client for the specified type.
     *
     * @param contributionType The type for the contributions to be returned.
     *
     * @returns The set of contribution Ids that are of the given type.
     */
    getContributionIdsByType: (contributionType: string) => string[];

    /**
     * Returns list of contributions which directly target the specified contributionId.
     *
     * @param contributionId The unique identifier for the contribution.
     *
     * @returns The set of contribution Ids that target the contributionId.
     */
    getContributionChildren: (contributionId: string, contributionType?: string) => string[] | undefined;

    /**
     * getContributionsByType is used to get a set of contributions based on the type of
     * the contribution. This will return all contributions that are currently available
     * on the client for the specified type.
     *
     * @param contributionType The type for the contributions to be returned.
     *
     * @returns The set of contributions that are of the given type.
     */
    getContributionsByType: <T>(contributionType: string) => { [contributionId: string]: T };

    /**
     * Gets details about the contribution's provider (e.g. extension)
     */
    getContributionProvider: (contributionId: string) => IContributionProviderDetails | undefined;

    /**
     * getData<T> is used to retrieve data returned from a data-provider with the
     * given contributionId and requestParameters.
     *
     * @param contributionId The id of the data provider contribution
     * @param requestParameters Optional context to provide to the data provider
     */
    getData: <T>(contributionId: string, requestParameters?: { [key: string]: any }) => T | undefined;

    /**
     * getDataAsync is used to retrieve data provider via a data-provider through
     * a promise. Unlike getData which returns only data that is returned before
     * the call and will not attempt to resolve data that is not available at page
     * generation time, getDataAsync will attempt to resolve failed server providers
     * or client providers. If the contributionId is not known or doesnt have
     * details about how to resolve it, undefined is returned.
     *
     * @param contributionId The contributionId of the data provider.
     *
     * @param requestParameters Parameters to use when fetching data.
     *
     * @param ignoreCache If true, call server for requested data. The result will still be remembered
     *  for subsequent calls that want the same data without ignoring the cache.
     *
     * @param serviceInstanceType Optional id of the service instance to make the REST call to. Defaults to the service hosting the web UI.
     */
    getDataAsync: <TData, TRequestData = { [key: string]: any }>(
        contributionId: string,
        requestParameters?: TRequestData,
        ignoreCache?: boolean,
        serviceInstanceType?: string, 
        options?: IVssRestClientOptions
    ) => Promise<TData | undefined>;

    /**
     * getSharedData<T> is used to retrieve shared data that was populated during the
     * page request and all subsequent data provider queries from the client (results are merged together).
     *
     * @param sharedDataKey The key of the shared data to return
     */
    getSharedData: <T>(sharedDataKey: string) => T | undefined;

    /**
     * getService is used to get a service defined as a contributed service. The contributed
     * service supplied to the function should be a contribution that conforms to the
     * IContributedService interface.
     *
     * This allows the service to defines dependencies through contributions. The actual name
     * of the service created is defined by the serviceName property in the contribution.
     *
     * @param contributionId The contributionId of the contributed service. This will be
     *  resolved before continuning with the service creation.
     *
     * @param remoteOnly If true, only return the service if it is marked as remotable.
     *
     * @returns A promise that resolves to the created service of type T or undefined if the
     *  contribution, any of its dependencies or the service is not resolvable.
     */
    getService: <T>(contributionId: string, remoteOnly?: boolean) => Promise<T | undefined>;

    /**
     * getServiceSync is used to get a service defined as a contributed service. The contributed
     * service supplied to the function should be a contribution that conforms to the
     * IContributedService interface.
     *
     * This allows the service to defines dependencies through contributions. The actual name
     * of the service created is defined by the serviceName property in the contribution.
     *
     * @param contributionId The contributionId of the contributed service. This will be
     *  resolved before continuning with the service creation.
     *
     * @param remoteOnly If true, only return the service if it is marked as remotable.
     *
     * @returns The resolved service of type T or undefined if the contribution,
     *  any of its dependencies or the service has not yet resolved.
     */
    getServiceSync: <T>(contributionId: string, remoteOnly?: boolean) => T | undefined;

    /**
     * getServiceEx is used to get a service defined as a contributed service. The contributed
     * service supplied to the function should be a contribution that will register the service.
     * This will will return the service defined in the contributedService parameter.
     *
     * This allows the service to defines dependencies through contributions. The actual name
     * of the service created is defined by the serviceName property in the contribution.
     *
     * @param contributionId The contributionId which will register the contributed service. This will be
     *  resolved before continuning with the service creation.
     *
     * @param contributedService The contributedService to return.
     *
     * @param remotableOnly If true, only return a first-party service if it is marked as remotable. This
     *  generally only needs to be specified in our external content host when it is getting a service
     *  instance that it will pass to the remote/iframed content.
     *
     * @returns A promise that resolves to the created service of type T or undefined if the
     *  contribution, any of its dependencies or the service is not resolvable.
     */
    getServiceEx<T>(contributionId: string, contributedService: IContributedService, remotableOnly?: boolean): Promise<T | undefined>;

    /**
     * Gets any child contributions for the given contribution id (of the specified type)
     * and instantiates the services specified by those contributions. Waits for all
     * services to be resolved before returning an array of the resolved services.
     *
     * @param contributionId The unique identifier of the parent contribution.
     *
     * @param contributionType The contribution type of the contributed services to return.
     */
    getChildServices<T>(contributionId: string, contributionType: string): Promise<{ id: string; service: T }[]>;

    /**
     * Gets any child contributions for the given contribution id (of the specified type)
     * and instantiates the services specified by those contributions. Returns an array of results
     * with the promise for each service so that the caller can leverage each service as it is
     * resolved individually.
     *
     * @param contributionId The unique identifier of the parent contribution.
     *
     * @param contributionType The contribution type of the contributed services to return.
     */
    getChildServicesEx<T>(contributionId: string, contributionType: string): { id: string; service: Promise<T | undefined> }[];

    /**
     * Used to surface exceptions that have been thrown by data providers that
     * the contribution processes. A consumer of this service can subscribe to changes
     * to this set of exceptions, but this provides a way to return the set that has
     * been encountered before the consumer has had a chance to subscribe.
     *
     * @returns A dictionary mapping the contribution id of the data provider to
     * an object representing the exception that was encountered on the server.
     */
    getDataProviderExceptions(): { [contributionId: string]: IServerException };

    /**
     * Determine whether or not the specified contribution has already been resolved.
     * For example, for content contributions, whether or not the specified content has been loaded
     * by the browser. For data provider contributions, whether or not the data is currently available.
     *
     * If this method returns true, a call to getContributionAsync for this contribution id will
     * NOT result in a network request.
     *
     * @param contributionId Id of the contribution to check
     */
    isContributionResolved(contributionId: string): boolean;

    /**
     * Returns true if the contribution comes from a trusted provider. Such contributions can
     * avoid being iframed.
     *
     * @param contributionId Id of the contribution to check
     */
    isContributionTrusted(contributionId: string): boolean;
}

interface IContributionRelationships {
    children: { [contributionId: string]: true };
    parents: { [contributionId: string]: true };
}

interface IContributionData {
    contributions: { [contributionId: string]: any };
    relationships: { [contributionId: string]: IContributionRelationships };
    types: { [contributionType: string]: { [contributionId: string]: true } };
    providers: { [providerId: string]: IContributionProviderDetails };
}

//
// NOTE: We are using a sub-set of the values from the IPageData data provider in
// the core of the contribution service. We can't use the IPageDataService since it
// would cause a circular dependency.
//
interface IContributionPageData {
    serviceInstanceType: string;
}

interface AccessTokenMetadata {
    rawData: string;
    expirationUtc: Date;
}

/** The "system" contribution property for provider attributes */
const ContributionAttributesProperty = "::Attributes";

/** Contribution provider attributes */
const enum ContributionAttributes {
    Trusted = 0x40
}

export class VssContributionService
    extends VssObservableService<
    IDataProviderQueryStartingEvent | IDataProviderQueryCompletedEvent,
    ContributionServiceEvent.DataProviderQueryStarting | ContributionServiceEvent.DataProviderQueryCompleted
    >
    implements IVssContributionService {
    // Current state of the dataProvider values.
    private dataProviderExceptions: { [contributionId: string]: IServerException } = {};
    private clientProviders: { [providerKey: string]: ClientDataProviderQuery } = {};
    private dataProviders: { [contributionId: string]: { [parametersKey: string]: any } } = {};
    private dataProviderScope: { name: string; value: string };
    private sharedData: { [key: string]: any } = {};

    // Track the set of pending contribution hierarchy queries
    private pendingContributions: { [contributionId: string]: Promise<void> } = {};

    // Current state of the contributions resolved for the page.
    private contributionPageData: IContributionPageData | undefined;

    private contentService: IVssContentService;
    private loadedContentContributions: { [contributionId: string]: ICompletedContribution } = {};

    public _serviceStart(pageContext: IVssPageContext) {
        super._serviceStart(pageContext);

        // Track loaded content contributions so that we don't make REST calls later if they are specified as dependencies
        this.contentService = this.pageContext.getService<IVssContentService>("IVssContentService");
        this.contentService.subscribe(this.onContributionLoaded, "contributionComplete");

        // First load all data from the initial page into the currently contributed data.
        const dataProviders = readJsonScript<DataProviderResult>("dataProviders", true);
        if (dataProviders) {
            this._applyData(dataProviders.data, dataProviders.sharedData, dataProviders.exceptions, dataProviders.clientProviders, undefined);

            // Set the scope using initial data provider result
            if (!this.dataProviderScope) {
                this.dataProviderScope = {
                    name: dataProviders.scopeName,
                    value: dataProviders.scopeValue
                };
            }
        } else {
            console.warn("No data providers found");
        }

        // Get the contributed data from the initial page download to initialize the
        // state of the contributions for this page.
        this.contributionPageData = this.getData<IContributionPageData>("ms.vss-web.page-data");
    }

    public _serviceEnd(pageContext: IVssPageContext) {
        super._serviceEnd(pageContext);

        this.contentService.unsubscribe(this.onContributionLoaded, "contributionComplete");
    }

    public async executeCommand<T>(commandId: string, ...args: any[]): Promise<T | null> {
        // Get the service defined by the command object and execute the supplied methodName.
        const commandContribution = await this.getContributionAsync<IContributedCommand>(commandId);
        if (commandContribution) {
            return this.executeCommandEx<T>(commandId, commandContribution, args);
        }

        return Promise.resolve(null);
    }

    public async executeCommandEx<T>(contributionId: string, command: IContributedCommand, ...args: any[]): Promise<T | null> {
        let result: T | null = null;

        // Get the service defined by the command object and execute the supplied methodName.
        if (command) {
            const commandService = await this.getServiceEx<any>(contributionId, command);
            if (commandService) {
                const method = commandService[command.methodName];
                if (method) {
                    result = method.apply(commandService, command.methodArguments || args);
                }
            }
        }

        return result;
    }

    public getContribution<T>(contributionId: string): T | undefined {
        let contribution: T | undefined = undefined;

        const contributionData = this.getContributionData();
        if (contributionData && contributionData.contributions) {
            contribution = contributionData.contributions[contributionId];
        }

        return contribution;
    }

    public async getContributionAsync<T>(
        contributionId: string,
        requestParameters?: { [key: string]: any },
        ignoreCache?: boolean,
        additionalRestOptions?: IVssRestClientOptions
    ): Promise<T | undefined> {
        const contributions = await this.getContributionsAsync<T>([contributionId], requestParameters, ignoreCache, additionalRestOptions);
        return contributions[0];
    }

    public async getContributionsAsync<T>(
        contributionIds: string[],
        requestParameters?: { [key: string]: any },
        ignoreCache?: boolean,
        additionalRestOptions?: IVssRestClientOptions
    ): Promise<Array<T | undefined>> {
        return this.getContributionsAsyncInternal<T>(contributionIds, requestParameters, ignoreCache);
    }

    public getContributionChildren(contributionId: string, contributionType?: string): string[] {
        let contributionMatches: string[] = [];

        const contributionData = this.getContributionData();
        if (contributionData && contributionData.relationships) {
            let contributionIds: { [contributionId: string]: true } = {};
            if (contributionData.relationships[contributionId]) {
                const relationships = contributionData.relationships[contributionId];
                if (relationships.children) {
                    contributionIds = relationships.children;
                }
            }

            // Loop through all the relationships and find any id which has passed in contribution id as a parent.
            for (const key in contributionData.relationships) {
                const relationship = contributionData.relationships[key];
                if (relationship && relationship.parents && relationship.parents[contributionId]) {
                    contributionIds[key] = true;
                }
            }

            // If a type is passed in, look up all contributions of that type and filter the list of contributions
            if (contributionType) {
                const contributionsByType = this.getContributionIdsByType(contributionType);

                // If no contributions found for specified type then return empty array.
                if (!contributionsByType || contributionsByType.length === 0) {
                    return contributionMatches;
                }

                for (const contributionByType of contributionsByType) {
                    if (contributionIds[contributionByType]) {
                        contributionMatches.push(contributionByType);
                    }
                }
            } else {
                contributionMatches = Object.keys(contributionIds);
            }
        }

        return contributionMatches;
    }

    public getContributionIdsByType(contributionType: string): string[] {
        let contributionIds: string[] = [];

        const contributionData = this.getContributionData();
        if (contributionData && contributionData.types && contributionData.types[contributionType]) {
            const typesSet = contributionData.types[contributionType];
            if (typesSet) {
                contributionIds = Object.keys(typesSet);
            }
        }

        return contributionIds;
    }

    public getContributionsByType<T>(contributionType: string): { [contributionId: string]: T } {
        const contributionIds = this.getContributionIdsByType(contributionType);
        const contributions: { [contributionId: string]: T } = {};

        const contributionData = this.getContributionData();
        if (contributionData) {
            for (const contributionId of contributionIds) {
                contributions[contributionId] = contributionData.contributions[contributionId] as T;
            }
        }

        return contributions;
    }

    public getContributionProvider(contributionId: string): IContributionProviderDetails | undefined {
        let provider: IContributionProviderDetails | undefined;

        const contributionData = this.getContributionData();
        if (contributionData && contributionData.providers) {
            const parts = contributionId.split(".");
            const providerId = parts[0] + "." + parts[1];
            provider = contributionData.providers[providerId];
            if (provider) {
                provider.id = providerId;
            }
        }
        return provider;
    }

    public getData<T>(contributionId: string, requestParameters?: { [key: string]: any }): T | undefined {
        let data: T | undefined = undefined;
        const parametersKey = this._getParametersKey(requestParameters);

        const providerDictionary = this.dataProviders[contributionId];
        if (providerDictionary) {
            data = providerDictionary[parametersKey] as T;
        }
        return data;
    }

    public async getDataAsync<TData, TRequestData = { [key: string]: any }>(
        contributionId: string,
        requestParameters?: TRequestData,
        ignoreCache?: boolean,
        serviceInstanceType?: string,
        additionalRestOptions?: IVssRestClientOptions
    ): Promise<TData | undefined> {
        let contributionData = ignoreCache ? undefined : this.getData<TData>(contributionId, requestParameters);

        // If the data is already available, we will return a resolved promise with the value.
        if (!contributionData) {
            let providerProperties: any;

            // Determine if there are details for resolving this contribution are available
            for (const providerKey in this.clientProviders) {
                if (this.clientProviders[providerKey].contributionIds.indexOf(contributionId) !== -1) {
                    const clientProvider = this.clientProviders[providerKey];
                    serviceInstanceType = serviceInstanceType || clientProvider.queryServiceInstanceType;
                    providerProperties = clientProvider.context.properties;
                    break;
                }
            }

            const parameters = { ...providerProperties, ...requestParameters };

            // Make a call to fetch the contribution hierarchy for this data provider and wait for the result
            await this.getContributionsAsyncInternal([contributionId], parameters, true, serviceInstanceType, additionalRestOptions);

            // Retrieve the data from the resolved providers now that we have completed the response processing.
            contributionData = this.getData<TData>(contributionId, parameters);
        }

        return Promise.resolve(contributionData);
    }

    public getSharedData<T>(sharedDataKey: string): T | undefined {
        return this.sharedData[sharedDataKey];
    }

    public getServiceSync<T>(contributionId: string, remoteOnly?: boolean): T | undefined {
        // First retrieve the contributed service for the specified contributionId.
        const contributedService = this.getContribution<IContributedService>(contributionId);
        if (contributedService) {
            // Check that all dependencies have loaded.
            if (contributedService.dependencies) {
                contributedService.dependencies.forEach((dependency: string) => {
                    if (!this.getContribution(dependency) && !this.isContentContributionLoaded(dependency)) {
                        return;
                    }
                });
            }

            return (this.pageContext as IVssPageContextInternal).getService<T>(contributedService.serviceName, remoteOnly, true);
        }
    }

    public async getService<T>(contributionId: string, remoteOnly?: boolean): Promise<T | undefined> {
        // First retrieve the contributed service for the specified contributionId.
        const contributedService = await this.getContributionAsync<IContributedService>(contributionId);
        if (contributedService) {
            return this.getServiceEx<T>(contributionId, contributedService, remoteOnly);
        }

        return Promise.reject(undefined);
    }

    public async getServiceEx<T>(contributionId: string, contributedService: IContributedService, remotableOnly?: boolean): Promise<T | undefined> {
        // First resolve the contributionId down to the IContributedService
        let dependenciesPromise: Promise<any>;

        // Resolve all dependencies of the contributed service
        if (contributedService.dependencies && contributedService.dependencies.length) {
            dependenciesPromise = this.getContributionsAsync(contributedService.dependencies);
        } else {
            dependenciesPromise = Promise.resolve();
        }

        // Once all dependencies are resolved, create the service and return it.
        await dependenciesPromise;
        const service = (this.pageContext as IVssPageContextInternal).getService<T>(contributedService.serviceName, remotableOnly, true);
        if (service) {
            return service;
        }

        await this.getContributionAsync("ms.vss-web.remote-service-providers");

        // The service is not yet defined. Check (remote) service providers.
        const services = await this.getChildServices<IRemoteServiceProvider>("ms.vss-web.remote-service-providers", "ms.vss-web.service");
        return services.length ? services[0].service.getService<T>(contributionId, contributedService) : undefined;
    }

    public getChildServices<T>(contributionId: string, contributionType: string): Promise<{ id: string; service: T }[]> {
        const services = this.getChildServicesEx<T>(contributionId, contributionType);

        return Promise.all(services.map(s => s.service)).then(p => {
            const results: { id: string; service: T }[] = [];
            for (let i = 0, l = services.length; i < l; i++) {
                if (p[i]) {
                    results.push({ id: services[i].id, service: p[i]! });
                }
            }
            return results;
        });
    }

    public getChildServicesEx<T>(contributionId: string, contributionType: string): { id: string; service: Promise<T | undefined> }[] {
        const result: { id: string; service: Promise<T | undefined> }[] = [];

        const childContributions = this.getContributionChildren(contributionId, contributionType);
        for (const childContribution of childContributions) {
            result.push({ id: childContribution, service: this.getService<T>(childContribution) });
        }

        return result;
    }

    public getDataProviderExceptions(): { [contributionId: string]: IServerException } {
        return this.dataProviderExceptions;
    }

    public isContributionResolved(contributionId: string): boolean {
        let resolved = false;
        if (this.contributionPageData) {
            const contributionData = this.getContributionData();
            if (contributionData && contributionData.contributions) {
                const parametersKey = this._getParametersKey();
                if (!this.pendingContributions[contributionId + "/" + parametersKey]) {
                    resolved =
                        contributionData.contributions.hasOwnProperty(contributionId) ||
                        this.isContentContributionLoaded(contributionId) ||
                        this.getData(contributionId) !== undefined;
                }
            }
        }
        return resolved;
    }

    public isContributionTrusted(contributionId: string): boolean {
        let isTrusted = false;
        const contribution = this.getContribution<any>(contributionId);
        if (contribution) {
            const attributes = contribution[ContributionAttributesProperty] as number;
            if (attributes & ContributionAttributes.Trusted) {
                isTrusted = true;
            }
        }
        return isTrusted;
    }

    private isContentContributionLoaded(contributionId: string): boolean {
        const completedContribution = this.loadedContentContributions[contributionId];
        return completedContribution ? completedContribution.resolved : false;
    }

    private onContributionLoaded = (completedContribution: ICompletedContribution): void => {
        this.loadedContentContributions[completedContribution.contributionId] = completedContribution;
    };

    private getContributionData(): IContributionData | undefined {
        return this.getSharedData<IContributionData>("_contributions");
    }

    private async getContributionsAsyncInternal<T>(
        contributionIds: string[],
        requestParameters?: { [key: string]: any },
        ignoreCache?: boolean,
        serviceInstanceType?: string,
        additionalRestOptions?: IVssRestClientOptions
    ): Promise<Array<T | undefined>> {
        let contributionData = this.getContributionData();
        let existingContributions = (contributionData && contributionData.contributions) || {};
        const parametersKey = this._getParametersKey(requestParameters);

        // Fetch missing contributions
        if (this.contributionPageData) {
            // Build a list of the contribution ids that we haven't resolved yet
            const missingContributionIds = new Set<string>();
            const promises: Promise<void>[] = [];

            for (const contributionId of contributionIds) {
                // If we have a request for this contribution already in progress, use it
                const promise = this.pendingContributions[contributionId + "/" + parametersKey];
                if (promise) {
                    promises.push(promise);
                } else {
                    // Add to the list of contributions we are going to make a REST call for. We'll add this
                    // contribution if we are bypassing the cache OR if it is not an already-known contribution.
                    // We know about 3 types of contributions:
                    // 1. Inline contributions (existingContributions)
                    // 2. Content contributions (loadedContentContributions)
                    // 3. Data provider contributions (this.getData)
                    if (
                        ignoreCache ||
                        (!existingContributions.hasOwnProperty(contributionId) &&
                            !this.isContentContributionLoaded(contributionId) &&
                            this.getData(contributionId, requestParameters) === undefined)
                    ) {
                        missingContributionIds.add(contributionId);
                    }
                }
            }

            // Fetch the missing contributions
            if (missingContributionIds.size > 0) {
                const contributionQueryPromise = this.fetchContributionsAsync(
                    Array.from(missingContributionIds),
                    requestParameters,
                    serviceInstanceType,
                    additionalRestOptions
                );
                promises.push(contributionQueryPromise);

                for (const missingContributionId of missingContributionIds) {
                    this.pendingContributions[missingContributionId + "/" + parametersKey] = contributionQueryPromise;
                }
            }

            // Wait for existing and new promises to complete
            if (promises.length) {
                const result = await Promise.allSettled(promises);
                // if there were any promise rejections, we need to remove those promises from pendingContributions so
                // that we can try again later
                let exceptionFound = false;
                let reason: any;
                for (let i = 0; i < result.length; i++) {
                    const r = result[i];
                    if (r.status === "rejected") {
                        exceptionFound = true;
                        reason = r.reason;
                        for (const [key, value] of Object.entries(this.pendingContributions)) {
                            if (value === promises[i]) {
                                delete this.pendingContributions[key];
                            }
                        }
                    }
                }
                if (exceptionFound) {
                    throw reason;
                }
            }

            contributionData = this.getContributionData();
            existingContributions = (contributionData && contributionData.contributions) || {};

            for (const missingContributionId of missingContributionIds) {
                // Delete any pending promise that was created earlier since we have now populated contribution data for this contribution id.
                delete this.pendingContributions[missingContributionId + "/" + parametersKey];

                // If we succeeded in resolving this contributionId but it wasn't returned,
                // it means this contribution is not an inline contribution (not downloaded to the client).
                // We will place undefined in its place to prevent future resolutions.
                // NOTE: We wont have a type value for this contribution.
                if (contributionData && !contributionData.contributions.hasOwnProperty(missingContributionId)) {
                    contributionData.contributions[missingContributionId] = undefined;
                }
            }
        }

        // Return the resolved contributions
        const results: Array<T | undefined> = [];
        for (const contributionId of contributionIds) {
            const contribution = existingContributions[contributionId];
            results.push(contribution);
        }

        return results;
    }

    private async fetchContributionsAsync(
        contributionIds: string[],
        requestParameters?: { [key: string]: any },
        serviceInstanceType?: string,
        additionalRestOptions?: IVssRestClientOptions
    ): Promise<void> {
        // Create data provider context properties object and fire the event to allow plugins to add properties.
        const properties = { ...requestParameters };
        this._notify({ contributionIds, properties }, ContributionServiceEvent.DataProviderQueryStarting);

        const webContributionsClient = this.pageContext.getRestClient<IWebContributionsRestClient>("IWebContributionsRestClient", {
            serviceInstanceType: serviceInstanceType,
            ...additionalRestOptions
        });

        const result = await webContributionsClient.queryContributedHierarchy(
            {
                contributionIds,
                dataProviderContext: { properties }
            },
            this.dataProviderScope.name,
            this.dataProviderScope.value
        );

        this._applyData(result.dataProviders, result.dataProviderSharedData, result.dataProviderExceptions, undefined, requestParameters);

        // Only load content after we've processed the returned data provider data
        if (result.content) {
            await this.contentService.loadScripts(result.content);
        }

        this._notify(
            {
                dataProviders: result.dataProviders,
                dataProviderSharedData: result.dataProviderSharedData,
                dataProviderExceptions: result.dataProviderExceptions
            },
            ContributionServiceEvent.DataProviderQueryCompleted
        );
    }

    private _applyData(
        data: { [key: string]: any } | undefined,
        sharedData: { [key: string]: any } | undefined,
        exceptions: { [key: string]: DataProviderExceptionDetails } | undefined,
        clientProviders: { [key: string]: ClientDataProviderQuery } | undefined,
        requestParameters: any | undefined
    ): void {
        if (data) {
            const parametersKey = this._getParametersKey(requestParameters);
            for (const contributionId in data) {
                let providerDictionary = this.dataProviders[contributionId];
                if (!providerDictionary) {
                    this.dataProviders[contributionId] = providerDictionary = {};
                }
                providerDictionary[parametersKey] = data[contributionId];
            }
        }

        if (sharedData) {
            for (const sharedDataKey in sharedData) {
                let newValue = sharedData[sharedDataKey];

                // Merge this result with an existing value if there is one
                const existingValue = this.sharedData[sharedDataKey];
                if (existingValue) {
                    newValue = deepMergeWithOptions({ deepClone: false }, existingValue, newValue);
                }

                this.sharedData[sharedDataKey] = newValue;
            }
        }

        if (clientProviders) {
            for (const clientKey in clientProviders) {
                this.clientProviders[clientKey] = clientProviders[clientKey];
            }
        }

        if (exceptions) {
            for (const contributionId in exceptions) {
                const exception = exceptions[contributionId];
                this.dataProviderExceptions[contributionId] = exception;
                console.warn(
                    `ContributionData unavailable for '${contributionId}', ExceptionType: '${exception.exceptionType}', Reason: ${exception.message}, StackTrace: ${exception.stackTrace}`
                );
            }
        }
    }

    private _getParametersKey(parameters?: any) {
        return parameters ? JSON.stringify(parameters) : "{}";
    }
}

Services.add("IVssContributionService", { serviceFactory: VssContributionService });

/**
 * An event fired by the contributed event service
 */
export interface IContributedEvent<T> {
    /**
     * Name of the event
     */
    name: string;

    /**
     * Data associated with the event
     */
    data: T;
}

export interface IContributedEventSubscription {
    /**
     * Name of the event to subscribe to
     */
    eventName: string;

    /**
     * Id of a contributed command to exeucte when the event is fired.
     */
    commandId?: string;

    /**
     * The command to execute when the event is fired.
     */
    command?: IContributedCommand;
}

/**
 * A service to fire events and invoke contributed event-subscriptions
 */
export interface IVssContributionEventService extends IVssObservableService<IContributedEvent<any>, string> {
    /**
     * Fire an event which triggers contributed event-subscriptions.
     *
     * @param eventName Name of the event
     * @param eventData Data associated with the event
     * @param persistEvent If true, persist the event so that subscriptions which are added later for this event are immediately invoked.
     */
    fireEvent<T>(eventName: string, eventData: T, persistEvent: boolean): void;
}

export class VssContributedEventService extends VssObservableService<IContributedEvent<any>, string> implements IVssContributionEventService {
    private _lastSubscriptions: { [subscriptionId: string]: IContributedEventSubscription } = {};
    private _contributionService: IVssContributionService;

    public _serviceStart(pageContext: IVssPageContext) {
        super._serviceStart(pageContext);

        this._contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        this._contributionService.subscribe(this.onContributionDataLoaded, ContributionServiceEvent.DataProviderQueryCompleted);

        this._lastSubscriptions = this.loadContributedSubscriptions({});
    }

    public _serviceEnd(pageContext: IVssPageContext) {
        super._serviceEnd(pageContext);

        this._contributionService.unsubscribe(this.onContributionDataLoaded, ContributionServiceEvent.DataProviderQueryCompleted);
    }

    public fireEvent<T>(name: string, data: T, persistEvent: boolean): void {
        const ev: IContributedEvent<T> = { name, data };
        this._notify(ev, name, persistEvent);
    }

    private onContributionDataLoaded = (): void => {
        this._lastSubscriptions = this.loadContributedSubscriptions(this._lastSubscriptions);
    };

    private loadContributedSubscriptions(subscriptionsToIgnore: { [subscriptionId: string]: IContributedEventSubscription }): {
        [subscriptionId: string]: IContributedEventSubscription;
    } {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        const eventSubscriptions = this._contributionService.getContributionsByType<IContributedEventSubscription>("ms.vss-web.event-subscription");

        for (const subscriptionId in eventSubscriptions) {
            if (!subscriptionsToIgnore[subscriptionId]) {
                const subscription = eventSubscriptions[subscriptionId];
                this.subscribe((ev: IContributedEvent<any>) => {
                    if (subscription.command) {
                        contributionService.executeCommandEx(subscriptionId, subscription.command, ev);
                    } else if (subscription.commandId) {
                        contributionService.executeCommand(subscription.commandId, ev);
                    }
                }, subscription.eventName);
            }
        }

        return eventSubscriptions;
    }
}

Services.add("IVssContributionEventService", { serviceFactory: VssContributedEventService });
